import React, { useRef, useState } from 'react';
import { FileUploadProps } from '../../../types/DesignSystem/FileUpload';
import FieldTitle from '../FieldTitle/FieldTitle';
import Typography from '../Typography/Typography';
import { Upload } from '../Iconography/Icons';
import { FOCUS_RING } from '../focusRing';
import { useMergedRef } from '../useMergedRef';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';

/**
 * FileUpload (§ 214) — the dropzone, and only the dropzone: see the type file.
 *
 * The zone is a real button-shaped control: `role="button"`, in the tab order,
 * Enter/Space open the browse dialog, and the actual `<input type="file">` is
 * visually hidden but REAL — the browse dialog, `accept` filtering and form
 * `name` participation are all native behaviours worth keeping.
 */

/**
 * § 390 — does `file` satisfy `accept`? The attribute's own grammar: comma-separated MIME types
 * (`text/csv`), wildcards (`image/*`) and extensions (`.csv`), all case-insensitive. No `accept`
 * accepts everything. A file whose type the browser left blank can still match by extension.
 */
export const acceptsFile = (file: File, accept?: string): boolean => {
  const tokens = (accept ?? '').split(',').map((token) => token.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
};

/** Border decided ONCE — the fieldBorder arrangement. Precedence: error > drag > disabled > rest. */
const zoneBorder = (state: { error?: boolean; disabled?: boolean; drag?: boolean }): string => {
  if (state.error) return 'tw-border-tone-danger-glyph';
  if (state.drag) return 'tw-border-accent';
  if (state.disabled) return 'tw-border-edge';
  return 'tw-border-edge-strong';
};

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(function FileUpload({
  label,
  onFilesSelected,
  accept,
  onReject,
  multiple = false,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  prompt = 'Drag and drop, or browse',
  className = '',
  id,
  name,
  'data-testid': dataTestId,
  ...rest
}, forwardedRef) {
  const uid = React.useId();
  const inputId = id ?? `${uid}-input`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  /*
    § 302 — `required` reaches assistive technology. It used to reach FieldTitle's
    asterisk only, and that mark is aria-hidden by design (FieldTitle's own docblock
    says the CONTROL must carry the fact). `aria-required` is not valid on
    `role="button"`, so the dropzone is DESCRIBED as required instead: an sr-only word
    joined into its `aria-describedby`, beside the helper text.
  */
  const requiredId = `${uid}-required`;
  /*
    § 404 — the dropzone, the one part a keyboard or screen reader reaches, is NAMED by the
    field's label. The label's `htmlFor` named only the hidden file input, so the zone took its
    name from its own content: "Drag and drop, or browse.csv,.xlsx" (measured in Chromium's
    accessibility tree), the field's purpose nowhere in it and the accept list run into the
    prompt. The name is now the label and the prompt; the accept list is a description of its
    own, after the required word and the messages.
  */
  const labelId = `${uid}-label`;
  const promptId = `${uid}-prompt`;
  const acceptId = `${uid}-accept`;
  const describedBy = [
    required ? requiredId : undefined,
    ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
    accept ? acceptId : undefined,
  ].filter(Boolean).join(' ') || undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const setRefs = useMergedRef<HTMLInputElement>(inputRef, forwardedRef);

  const deliver = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    /* § 390 — opt-in validation: with `onReject`, only files that satisfy `accept` are
       selected, on either path; the rest are reported. Without it, nothing is filtered. */
    const accepted = onReject ? files.filter((file) => acceptsFile(file, accept)) : files;
    const rejected = onReject ? files.filter((file) => !acceptsFile(file, accept)) : [];
    if (rejected.length > 0) onReject?.(rejected);
    if (accepted.length === 0) return;
    /* Single mode delivers AT MOST ONE, whatever was dragged — the native input
       already enforces this for browse; the drop path must match it. */
    onFilesSelected(multiple ? accepted : accepted.slice(0, 1));
  };

  const browse = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  return (
    <div className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the file input's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          disabled={disabled}
          htmlFor={inputId}
          labelId={labelId}
        />
      )}

      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- role="button" + tabIndex + the Enter/Space handler make it interactive */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-labelledby={label !== '' ? `${labelId} ${promptId}` : promptId}
        aria-describedby={describedBy}
        data-testid={dataTestId ? `${dataTestId}-dropzone` : undefined}
        onClick={browse}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would also scroll the page — this is a button, so it must not.
            event.preventDefault();
            browse();
          }
        }}
        onDragOver={(event) => {
          // preventDefault is what makes the element a legal drop target at all.
          event.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          if (disabled) return;
          deliver(event.dataTransfer?.files ?? null);
        }}
        /*
          DASHED is the dropzone's identity — the one dashed border in the
          system, so "you can drop here" reads at a glance. Rest sits on the
          control tier (edge-strong, § 25 — this IS an interactive boundary);
          the drag state moves to accent on the § 207 selection tint, the
          same "this is about to take" grammar a selected row wears.
        */
        className={`tw-border-2 tw-border-dashed tw-rounded-ctl tw-p-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-center tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}
          ${zoneBorder({ error: invalid, disabled, drag: dragActive })}
          ${disabled
            ? 'tw-bg-surface-sunken tw-text-ink-disabled tw-cursor-not-allowed'
            : dragActive
              ? 'tw-bg-accent-tint tw-cursor-pointer'
              : 'tw-bg-surface hover:tw-bg-surface-hover tw-cursor-pointer'
          }`}
      >
        <Upload
          size={20}
          aria-hidden="true"
          className={disabled ? 'tw-text-ink-disabled' : dragActive ? 'tw-text-accent' : 'tw-text-ink-tertiary'}
        />
        <Typography
          variant="title"
          /* § 346 — the prompt is a sentence, not a heading: the variant's default `<h5>`
             put a fifth-level heading inside a `role="button"`, whose children are
             presentational anyway. A flex item either way, so the box is unchanged. */
          as="span"
          id={promptId}
          className={disabled ? 'tw-text-ink-disabled' : dragActive ? 'tw-text-accent' : 'tw-text-ink'}
        >
          {prompt}
        </Typography>
        {accept && (
          <Typography variant="caption-metadata" id={acceptId} className={disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'}>
            {accept}
          </Typography>
        )}
      </div>

      <input
        /* rest first — the Button.tsx rule. */
        {...rest}
        ref={setRefs}
        id={inputId}
        type="file"
        name={name}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        /* § 356 — the labelled control carries the invalid state; the dropzone is a
           `role="button"`, where `aria-invalid` is not a supported attribute. */
        aria-invalid={invalid || undefined}
        data-testid={dataTestId}
        onChange={(event) => {
          deliver(event.target.files);
          /* Reset so picking the SAME file again still fires change — the
             classic silent re-upload failure. */
          event.target.value = '';
        }}
        /* Visually hidden but real — never `display:none`, which some browsers
           exclude from form participation and focus routing. */
        className="tw-sr-only"
        tabIndex={-1}
      />

      {/* § 302 — the word the dropzone's `aria-describedby` points at while required. */}
      {required && (
        <span id={requiredId} className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-required` : undefined}>
          Required
        </span>
      )}

      {/* § 302 — the one helper seam: an errored dropzone announces like every other field. */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />
    </div>
  );
});

export default FileUpload;
